import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Search, ShieldCheck } from "lucide-react";
import { useGetList } from "ra-core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Sale, UserRole } from "../types";

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  super_admin: { label: "Super Admin", color: "bg-purple-100 text-purple-800" },
  sales_manager: { label: "Sales Manager", color: "bg-blue-100 text-blue-800" },
  sales_rep: { label: "Sales Rep", color: "bg-cyan-100 text-cyan-800" },
  finance_manager: { label: "Finance Manager", color: "bg-green-100 text-green-800" },
  procurement_officer: { label: "Procurement Officer", color: "bg-orange-100 text-orange-800" },
  designer: { label: "Designer", color: "bg-pink-100 text-pink-800" },
};

function getUserInitials(record: Sale) {
  return `${record.first_name?.[0] ?? ""}${record.last_name?.[0] ?? ""}`.toUpperCase();
}

export const UsersListPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: users, isPending } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });
  const { data: roles } = useGetList<UserRole>("roles", {
    pagination: { page: 1, perPage: 50 },
  });

  const filtered = (users || []).filter((u) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.department?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Users</h2>
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, department..."
            className="w-full pl-8 pr-3 py-2 text-sm border rounded-md bg-background"
          />
        </div>
      </div>

      <Card className="border-none shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {isPending ? (
            <div className="py-12 text-center text-muted-foreground text-sm">Loading users…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm italic">No users found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Department</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const roleName = roles?.find((r) => r.slug === u.role)?.name;
                  const roleInfo = u.role ? ROLE_LABELS[u.role] : null;
                  return (
                    <tr
                      key={u.id}
                      onClick={() => navigate(`/sales/${u.id}/show`)}
                      className="border-b last:border-0 hover:bg-muted cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                          {getUserInitials(u)}
                        </div>
                        <span className="font-medium text-foreground">{u.first_name} {u.last_name}</span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3">
                        {roleInfo ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}>
                            <ShieldCheck size={11} />
                            {roleInfo.label}
                          </span>
                        ) : roleName ? (
                          <span className="text-xs text-muted-foreground">{roleName}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {u.department ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <Building2 size={12} /> {u.department}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.disabled ? (
                          <Badge variant="outline" className="border-orange-400 text-orange-600 text-xs">Disabled</Badge>
                        ) : (
                          <Badge variant="outline" className="border-emerald-400 text-emerald-600 text-xs">Active</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

UsersListPage.path = "/users";
